import { Bookmark, Heart, MessageCircle, Send } from "lucide-react";
import { ScaledLivePreview } from "@/components/InstagramMockup";

interface LandingLivePreviewProps {
  src: string;
  title: string;
  format: "feed" | "story";
  clubName?: string;
  crestUrl?: string;
  compact?: boolean;
  likes?: number;
  caption?: string;
  showSocialDetails?: boolean;
}

function Crest({ crestUrl, clubName, size = "feed" }: Pick<LandingLivePreviewProps, "crestUrl" | "clubName"> & { size?: "feed" | "story" }) {
  return (
    <span className={`flex aspect-square shrink-0 items-center justify-center overflow-hidden rounded-full ${size === "story" ? "w-[22px]" : "w-8 border border-instagram-line bg-instagram-screen"}`}>
      {crestUrl ? <img src={crestUrl} alt="" className="h-full w-full object-contain" /> : null}
    </span>
  );
}

export default function LandingLivePreview({
  src,
  title,
  format,
  clubName = "SV Beispielkirchen",
  crestUrl,
  compact = false,
  likes = 247,
  caption,
  showSocialDetails = false,
}: LandingLivePreviewProps) {
  if (format === "story") {
    const story = (
      <div className={`relative mx-auto aspect-[9/16] w-full overflow-hidden rounded-2xl border-[8px] border-instagram-shell bg-instagram-shell shadow-sm ${compact ? "max-w-[210px]" : "max-w-[270px]"}`}>
        <ScaledLivePreview src={src} title={title} height={1920} />
        {showSocialDetails ? (
          <>
            <div className="pointer-events-none absolute inset-x-0 top-0 z-10 h-[60px] bg-gradient-to-b from-instagram-overlay to-transparent" />
            <div className="absolute inset-x-3 top-2 z-20 h-0.5 overflow-hidden rounded-full bg-instagram-screen/40">
              <div className="h-full w-2/3 rounded-full bg-instagram-screen" />
            </div>
            <div className="absolute inset-x-3 top-5 z-20 flex items-center gap-2 text-[12px] font-semibold leading-tight text-instagram-screen drop-shadow-md">
              <Crest crestUrl={crestUrl} clubName={clubName} size="story" />
              <span className="max-w-[70%]">{clubName}</span>
            </div>
            <div className="absolute bottom-[14px] left-3 z-20 flex h-[34px] items-center rounded-full border border-instagram-screen bg-instagram-overlay/35 px-4 text-[12px] text-instagram-screen">Antworten</div>
          </>
        ) : null}
      </div>
    );

    if (!showSocialDetails) return story;

    return (
      <div className="grid h-full grid-rows-[470px_110px]">
        <div className="flex items-center justify-center">
          {story}
        </div>
        <div className="px-3 pt-2 text-xs text-muted-foreground">
          Story · 24 Stunden sichtbar
        </div>
      </div>
    );
  }

  return (
      <div className={showSocialDetails ? "grid h-full min-w-0 grid-rows-[535px_1fr]" : "w-full min-w-0"}>
      <div className="flex min-w-0 items-center">
        <div className="min-w-0 w-full overflow-hidden rounded-xl border border-instagram-line bg-instagram-screen text-instagram-ink shadow-sm">
          <div className="flex h-14 items-center gap-2.5 px-3">
            <Crest crestUrl={crestUrl} clubName={clubName} />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[13px] font-bold">beispielverein</span>
            </span>
            <span className="font-bold">···</span>
          </div>
          <ScaledLivePreview src={src} title={title} height={1350} />
          <div className="flex h-11 items-center justify-between px-3">
            <div className="flex items-center gap-3">
              <Heart className="h-5 w-5" />
              <MessageCircle className="h-5 w-5" />
              <Send className="h-5 w-5" />
            </div>
            <Bookmark className="h-5 w-5" />
          </div>
        </div>
      </div>
      {showSocialDetails ? (
        <div className="min-w-0 space-y-1 px-3 pt-5 text-[13px] leading-[1.4]">
          <div className="flex min-w-0 items-start">
            <span className="mr-2 flex shrink-0">
              {['M', 'S', 'L'].map((initial, index) => (
                <span key={initial} className={`flex h-[18px] w-[18px] items-center justify-center rounded-full border border-instagram-screen bg-instagram-muted text-[9px] font-semibold ${index > 0 ? "-ml-1.5" : ""}`}>{initial}</span>
              ))}
            </span>
            <span className="min-w-0 break-words">Gefällt <strong>max.mueller</strong> und <strong>246 weiteren Personen</strong></span>
          </div>
          <p className="break-words"><span className="font-bold">beispielverein</span> {caption}</p>
          <p className="text-muted-foreground">Alle 18 Kommentare ansehen</p>
          <p className="text-[10px] uppercase tracking-[0.04em] text-muted-foreground">Vor 2 Stunden</p>
        </div>
      ) : null}
    </div>
  );
}